import type { ReactNode } from "react";
import { motion } from "framer-motion";

/**
 * Two-sided 3D card. Renders the backing until `flipped` turns true, then
 * rotates on Y to show the front — the core beat of the pack-opening ceremony.
 */
export function FlipCard({
  flipped,
  back,
  front,
  onClick,
  className = "",
  duration = 0.7,
  disabled = false,
  ariaLabel,
}: {
  flipped: boolean;
  back: ReactNode;
  front: ReactNode;
  onClick?: () => void;
  className?: string;
  duration?: number;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const interactive = !!onClick && !disabled;

  return (
    <div className={`stage-3d relative ${className}`}>
      <motion.div
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={ariaLabel}
        onClick={interactive ? onClick : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        className={`preserve-3d relative h-full w-full ${interactive ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink/60" : ""}`}
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flip-face absolute inset-0 overflow-hidden">{back}</div>
        <div className="flip-face flip-face-back absolute inset-0 overflow-hidden">{front}</div>
      </motion.div>
    </div>
  );
}
